import React, { useState, useEffect } from "react";
import {
  X,
  Loader,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Calendar,
  Home,
} from "../utils/icons";
import { supabase } from "../lib/supabase";
// Removed EmailJS import - now using Netlify Forms
import SEO from "./SEO";

interface SurveyPopupProps {
  surveyId: string;
  isOpen: boolean;
  onClose: () => void;
  preloadedData?: {
    survey: any;
    questions: Question[];
    choices: Record<string, Choice[]>;
  } | null;
}

interface Question {
  id: string;
  question_text: string;
  question_type:
    | "multiple_choice"
    | "yes_no"
    | "contact_form"
    | "short_answer"
    | "text_only";
  order_number: number;
  is_required: boolean;
  choices?: Choice[];
}

interface Choice {
  id: string;
  choice_text: string;
  order_number: number;
}

interface SurveyResponse {
  [questionId: string]: string | string[];
}

interface OtherResponses {
  [questionId: string]: string;
}

function SurveyPopup({
  surveyId,
  isOpen,
  onClose,
  preloadedData = null,
}: SurveyPopupProps) {
  console.log(
    "SurveyPopup rendered with isOpen:",
    isOpen,
    "surveyId:",
    surveyId,
  );
  const [survey, setSurvey] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<SurveyResponse>({});
  const [otherResponses, setOtherResponses] = useState<OtherResponses>({});
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [respondentId, setRespondentId] = useState<string | null>(null);

  useEffect(() => {
    // Generate a respondent ID when the component mounts
    if (!respondentId) {
      setRespondentId(crypto.randomUUID());
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (preloadedData) {
        // Use preloaded data if available
        console.log("Using preloaded survey data");
        setSurvey(preloadedData.survey);
        setQuestions(
          preloadedData.questions.map((question) => {
            if (
              question.question_type === "multiple_choice" &&
              preloadedData.choices[question.id]
            ) {
              return {
                ...question,
                choices: preloadedData.choices[question.id],
              };
            }
            return question;
          }),
        );
        setLoading(false);
      } else if (surveyId) {
        // Fetch data if not preloaded
        console.log("Fetching survey data on demand");
        fetchSurveyData();
      }
    }
  }, [isOpen, surveyId, preloadedData]);

  const fetchSurveyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch survey details
      const { data: surveyData, error: surveyError } = await supabase
        .from("surveys")
        .select("*")
        .eq("id", surveyId)
        .single();

      if (surveyError) {
        throw new Error(`Failed to load survey: ${surveyError.message}`);
      }

      setSurvey(surveyData);

      // Fetch questions
      const { data: questionsData, error: questionsError } = await supabase
        .from("questions")
        .select("*")
        .eq("survey_id", surveyId)
        .order("order_number");

      if (questionsError) {
        throw new Error(`Failed to load questions: ${questionsError.message}`);
      }

      // Fetch choices for multiple choice questions
      const multipleChoiceQuestions =
        questionsData?.filter((q) => q.question_type === "multiple_choice") ||
        [];

      const questionsWithChoices = [...(questionsData || [])];

      if (multipleChoiceQuestions.length > 0) {
        for (const question of multipleChoiceQuestions) {
          const { data: choicesData, error: choicesError } = await supabase
            .from("choices")
            .select("*")
            .eq("question_id", question.id)
            .order("order_number");

          if (choicesError) {
            console.error(
              `Failed to load choices for question ${question.id}:`,
              choicesError,
            );
            continue;
          }

          // Find the question in our array and add choices
          const questionIndex = questionsWithChoices.findIndex(
            (q) => q.id === question.id,
          );
          if (questionIndex !== -1) {
            questionsWithChoices[questionIndex].choices = choicesData;
          }
        }
      }

      setQuestions(questionsWithChoices);
    } catch (err) {
      console.error("Error fetching survey data:", err);
      setError(err instanceof Error ? err.message : "Failed to load survey");
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = () => {
    // Validate current question if required
    const currentQuestion = questions[currentQuestionIndex];

    if (currentQuestion.is_required) {
      // For contact form questions, check required fields individually
      if (currentQuestion.question_type === "contact_form") {
        const errors: Record<string, string> = {};

        if (!contactInfo.name) {
          errors.name = "Name is required";
        }

        if (!contactInfo.email) {
          errors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
          errors.email = "Please enter a valid email address";
        }

        if (!contactInfo.company) {
          errors.company = "Company is required";
        }

        // Phone is optional, so no validation needed

        if (Object.keys(errors).length > 0) {
          setValidationErrors({
            ...validationErrors,
            ...errors,
          });
          return;
        }

        // Store contact info as response
        handleResponseChange(currentQuestion.id, JSON.stringify(contactInfo));
      } else {
        // For other question types
        const response = responses[currentQuestion.id];

        if (!response || (Array.isArray(response) && response.length === 0)) {
          setValidationErrors({
            ...validationErrors,
            [currentQuestion.id]: "This question requires an answer",
          });
          return;
        }
      }
    }

    // Clear validation error for this question
    const newValidationErrors = { ...validationErrors };
    delete newValidationErrors[currentQuestion.id];
    setValidationErrors(newValidationErrors);

    // Move to next question or submit if it's the last question
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Create a synthetic event object to prevent the TypeError
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleResponseChange = (
    questionId: string,
    value: string | string[],
  ) => {
    setResponses({
      ...responses,
      [questionId]: value,
    });

    // Clear validation error if any
    if (validationErrors[questionId]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[questionId];
      setValidationErrors(newValidationErrors);
    }
  };

  const handleContactInfoChange = (field: string, value: string) => {
    setContactInfo({
      ...contactInfo,
      [field]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      if (!respondentId) {
        throw new Error("Missing respondent ID");
      }

      // Prepare all database operations in parallel arrays
      const dbOperations = [];
      const emailData = {
        to_email: "",
        from_name: contactInfo.name || "Survey Respondent",
        from_email: contactInfo.email || "no-email@provided.com",
        subject: "New AI Audit Survey Submission",
        message: "",
        reply_to: contactInfo.email || "no-reply@one80labs.com",
      };

      // Check if this survey has already been completed by this respondent
      const { data: existingCompletion, error: completionCheckError } =
        await supabase
          .from("survey_completions")
          .select("*")
          .eq("survey_id", surveyId)
          .eq("respondent_id", respondentId)
          .maybeSingle();

      if (completionCheckError) {
        console.error(
          "Error checking for existing completion:",
          completionCheckError,
        );
        throw completionCheckError;
      }

      if (existingCompletion) {
        console.log("Survey already completed by this respondent");
        setIsSubmitted(true);
        setIsSubmitting(false);
        return;
      }

      // Split the name into first_name and last_name
      const nameParts = contactInfo.name.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || contactInfo.name.trim(); // Use full name as last name if no space

      // Add contact information to operations queue
      dbOperations.push(
        supabase.from("crm_contacts").insert([
          {
            first_name: firstName,
            last_name: lastName,
            email: contactInfo.email,
            phone: contactInfo.phone,
            company: contactInfo.company,
            source: "AI Audit Survey",
            status: "prospect",
          },
        ]),
      );

      // Record survey completion first to prevent race conditions
      try {
        const { error: completionError } = await supabase
          .from("survey_completions")
          .insert([{ survey_id: surveyId, respondent_id: respondentId }]);

        if (completionError) {
          // If it's a duplicate key error, the survey was already completed
          if (completionError.code === "23505") {
            console.log(
              "Survey already completed (detected from insert error)",
            );
            setIsSubmitted(true);
            return;
          }
          throw completionError;
        }
      } catch (completionError: any) {
        // Handle the case where the error is due to a duplicate key
        if (
          completionError.code === "23505" ||
          (completionError.message &&
            completionError.message.includes("duplicate key"))
        ) {
          console.log("Survey already completed (caught exception)");
          setIsSubmitted(true);
          return;
        }
        // Re-throw other errors
        throw completionError;
      }

      // Prepare response summary for email
      const responseSummary: string[] = [];

      // Save all responses
      const responseInserts: any[] = [];
      for (const questionId in responses) {
        const response = responses[questionId];
        if (response !== undefined && response !== null) {
          const responseData: any = {
            survey_id: surveyId,
            question_id: questionId,
            respondent_id: respondentId,
            view_mode: "popup",
          };

          // Find the question to determine its type
          const question = questions.find((q) => q.id === questionId);
          if (!question) continue;

          // Prepare response summary for email
          let displayResponse = "";

          // Handle different question types
          if (question.question_type === "multiple_choice") {
            if (Array.isArray(response)) {
              // Check if "Other" is selected and we have an other response
              if (response.includes("Other") && otherResponses[questionId]) {
                // Add the "Other" response with the text
                responseData.text_response = `Other: ${otherResponses[questionId]}`;
                responseInserts.push({ ...responseData });

                // Add to display response for email
                const otherText = `Other: ${otherResponses[questionId]}`;
                const otherChoices = response.filter((r) => r !== "Other");
                displayResponse = [...otherChoices, otherText].join(", ");
              }

              // For multi-select questions, save each choice as a separate response
              for (const choice of response) {
                // Skip "Other" as we've already handled it separately
                if (choice === "Other") continue;

                responseData.text_response = choice;
                responseInserts.push({ ...responseData });
              }

              // If display response wasn't set by "Other" handling
              if (!displayResponse) {
                displayResponse = response.join(", ");
              }

              continue; // Skip the regular insert below for multi-select
            } else {
              // Check if this is an "Other" response
              if (response === "Other" && otherResponses[questionId]) {
                responseData.text_response = `Other: ${otherResponses[questionId]}`;
                displayResponse = `Other: ${otherResponses[questionId]}`;
              } else {
                responseData.text_response = response;
                displayResponse = String(response);
              }
            }
          } else if (question.question_type === "yes_no") {
            responseData.text_response = response;
            displayResponse = String(response);
          } else if (question.question_type === "short_answer") {
            responseData.text_response = response;
            displayResponse = String(response);
          } else if (question.question_type === "contact_form") {
            // For contact form, store the JSON string as text_response
            responseData.text_response =
              typeof response === "string"
                ? response
                : JSON.stringify(response);
            // Skip contact form in the summary as we're using it separately
            continue;
          }

          // Add to response summary for email
          responseSummary.push(`${question.question_text}: ${displayResponse}`);

          // Save response (except for multi-select which is handled above)
          if (!Array.isArray(response)) {
            responseInserts.push(responseData);
          }
        }
      }

      // Batch insert all responses at once if there are any
      if (responseInserts.length > 0) {
        dbOperations.push(supabase.from("responses").insert(responseInserts));
      }

      // Execute all database operations in parallel
      await Promise.all(dbOperations);

      // Send notification via Netlify Forms
      try {
        // Submit to Netlify Forms
        const formData = new FormData();
        formData.append("form-name", "ai-survey");
        formData.append("name", contactInfo.name || "Survey Respondent");
        formData.append("email", contactInfo.email || "no-email@provided.com");
        formData.append("company", contactInfo.company || "Not provided");
        formData.append("phone", contactInfo.phone || "Not provided");
        formData.append("subject", "New AI Audit Survey Submission");
        formData.append(
          "message",
          `A new AI Audit survey has been submitted.\n\nContact Information:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company}\nPhone: ${contactInfo.phone || "Not provided"}\n\nSurvey Responses:\n\n${responseSummary.join("\n\n")}`,
        );

        const response = await fetch("/", {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          console.log("Survey notification sent via Netlify Forms");
        } else {
          console.warn("Failed to send survey notification via Netlify Forms");
        }
      } catch (error) {
        // Don't fail the submission if notification sending fails
        console.error("Error sending survey notification:", error);
      }

      // Close popup and show success message
      setIsSubmitted(true);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err instanceof Error ? err.message : "Failed to submit form");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when the popup is closed
  useEffect(() => {
    // Only reset form data when closing, not the survey data
    if (!isOpen && !preloadedData) {
      setCurrentQuestionIndex(0);
      setResponses({});
      setContactInfo({
        name: "",
        email: "",
        phone: "",
        company: "",
      });
      setOtherResponses({});
      setValidationErrors({});
      setError(null);
      setIsSubmitted(false);
      setIsSubmitting(false);
      // Generate a new respondent ID when the form is reset
      setRespondentId(crypto.randomUUID());
    }
  }, [isOpen, preloadedData]);

  const renderQuestionContent = (question: Question) => {
    switch (question.question_type) {
      case "multiple_choice":
        // Check if this is a "Check all that apply" question
        const isMultiSelect = question.question_text.includes(
          "(Check all that apply)",
        );

        // Initialize response as array if it's a multi-select question and not already set
        if (isMultiSelect && !responses[question.id]) {
          setResponses((prev) => ({
            ...prev,
            [question.id]: [],
          }));
        }

        return (
          <div className="space-y-4">
            {question.choices?.map((choice) => (
              <div key={choice.id} className="flex items-center">
                <input
                  type={isMultiSelect ? "checkbox" : "radio"}
                  id={choice.id}
                  name={question.id}
                  value={choice.choice_text}
                  checked={
                    isMultiSelect
                      ? Array.isArray(responses[question.id]) &&
                        (responses[question.id] as string[]).includes(
                          choice.choice_text,
                        )
                      : (responses[question.id] as string) ===
                        choice.choice_text
                  }
                  onChange={(e) => {
                    if (isMultiSelect) {
                      // For multi-select, maintain an array of selected values
                      const currentSelections = Array.isArray(
                        responses[question.id],
                      )
                        ? [...(responses[question.id] as string[])]
                        : [];

                      if (e.target.checked) {
                        // Add to selections
                        handleResponseChange(question.id, [
                          ...currentSelections,
                          choice.choice_text,
                        ]);
                      } else {
                        // Remove from selections
                        handleResponseChange(
                          question.id,
                          currentSelections.filter(
                            (item) => item !== choice.choice_text,
                          ),
                        );
                      }
                    } else {
                      // For single select, just use the value
                      handleResponseChange(question.id, e.target.value);
                    }
                  }}
                  className="h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
                />
                <label htmlFor={choice.id} className="ml-3 text-gray-700">
                  {choice.choice_text}
                </label>
              </div>
            ))}
            {/* Add "Other" option if needed */}
            <div className="flex items-center">
              <input
                type={isMultiSelect ? "checkbox" : "radio"}
                id={`${question.id}-other`}
                name={question.id}
                value="Other"
                checked={
                  isMultiSelect
                    ? Array.isArray(responses[question.id]) &&
                      (responses[question.id] as string[]).includes("Other")
                    : (responses[question.id] as string) === "Other"
                }
                onChange={(e) => {
                  if (isMultiSelect) {
                    // For multi-select, maintain an array of selected values
                    const currentSelections = Array.isArray(
                      responses[question.id],
                    )
                      ? [...(responses[question.id] as string[])]
                      : [];

                    if (e.target.checked) {
                      // Add to selections
                      handleResponseChange(question.id, [
                        ...currentSelections,
                        "Other",
                      ]);
                    } else {
                      // Remove from selections
                      handleResponseChange(
                        question.id,
                        currentSelections.filter((item) => item !== "Other"),
                      );
                    }
                  } else {
                    // For single select, just use the value
                    handleResponseChange(question.id, e.target.value);
                  }
                }}
                className="h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
              />
              <label
                htmlFor={`${question.id}-other`}
                className="ml-3 text-gray-700"
              >
                Other
              </label>
            </div>
            {(isMultiSelect
              ? Array.isArray(responses[question.id]) &&
                (responses[question.id] as string[]).includes("Other")
              : (responses[question.id] as string) === "Other") && (
              <input
                type="text"
                value={otherResponses[question.id] || ""}
                onChange={(e) =>
                  setOtherResponses({
                    ...otherResponses,
                    [question.id]: e.target.value,
                  })
                }
                placeholder="Please specify"
                className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D] sm:text-sm"
              />
            )}
          </div>
        );

      case "yes_no":
        return (
          <div className="space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name={question.id}
                value="Yes"
                checked={responses[question.id] === "Yes"}
                onChange={(e) =>
                  handleResponseChange(question.id, e.target.value)
                }
                className="h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name={question.id}
                value="No"
                checked={responses[question.id] === "No"}
                onChange={(e) =>
                  handleResponseChange(question.id, e.target.value)
                }
                className="h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
              />
              <span className="ml-2">No</span>
            </label>
          </div>
        );

      case "short_answer":
        return (
          <textarea
            value={(responses[question.id] as string) || ""}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            rows={4}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D] sm:text-sm"
            placeholder="Enter your answer here..."
          />
        );

      case "contact_form":
        return (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={contactInfo.name}
                onChange={(e) =>
                  handleContactInfoChange("name", e.target.value)
                }
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  validationErrors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                }`}
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.name}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={contactInfo.email}
                onChange={(e) =>
                  handleContactInfoChange("email", e.target.value)
                }
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  validationErrors.email
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                }`}
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.email}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                value={contactInfo.phone}
                onChange={(e) =>
                  handleContactInfoChange("phone", e.target.value)
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D] sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="company"
                className="block text-sm font-medium text-gray-700"
              >
                Company <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                value={contactInfo.company}
                onChange={(e) =>
                  handleContactInfoChange("company", e.target.value)
                }
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  validationErrors.company
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                }`}
              />
              {validationErrors.company && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.company}
                </p>
              )}
            </div>
          </div>
        );

      case "text_only":
        return null;

      default:
        return <p className="text-red-600">Unsupported question type</p>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {survey && (
        <SEO
          title={`${survey.title} | One80Labs Survey`}
          description={
            survey.description ||
            "Complete this survey from One80Labs to help us improve our services."
          }
          noindex={true} // Don't index popup surveys
        />
      )}

      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="p-6">
          {isSubmitted ? (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
              <CheckCircle className="w-16 h-16 text-[#F98B3D] mb-6" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Awesome! Your personalized AI Audit is on the way.
              </h1>
              <p className="text-lg text-gray-600 mb-8 max-w-md">
                We'll email you 3 custom AI recommendations for your business
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={onCalClick}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Free Consultation
                </button>
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                  <Home className="h-5 w-5 mr-2" />
                  Back to Lab
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-12 w-12 text-[#F98B3D] animate-spin mb-4" />
              <p className="text-gray-600">Loading survey...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {survey?.title || "AI Audit Survey"}
                </h2>
                {survey?.description && (
                  <p className="text-gray-600 mt-2">{survey.description}</p>
                )}

                {/* Progress indicator */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-[#F98B3D] h-2.5 rounded-full"
                      style={{
                        width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-right text-sm text-gray-500 mt-1">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </p>
                </div>
              </div>

              {questions.length > 0 ? (
                <div className="min-h-[300px]">
                  {/* Current question */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {questions[currentQuestionIndex].question_text}
                      {questions[currentQuestionIndex].is_required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </h3>

                    {/* Question content based on type */}
                    {renderQuestionContent(questions[currentQuestionIndex])}

                    {/* Validation error */}
                    {validationErrors[questions[currentQuestionIndex].id] && (
                      <p className="text-sm text-red-600 mt-2">
                        {validationErrors[questions[currentQuestionIndex].id]}
                      </p>
                    )}
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={handlePreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                      className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                        currentQuestionIndex === 0
                          ? "border-gray-200 text-gray-300 cursor-not-allowed"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Previous
                    </button>

                    <button
                      type="button"
                      onClick={handleNextQuestion}
                      disabled={isSubmitting}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
                    >
                      {currentQuestionIndex === questions.length - 1 ? (
                        isSubmitting ? (
                          "Submitting..."
                        ) : (
                          "Submit"
                        )
                      ) : (
                        <>
                          Next
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">
                    No questions found for this survey.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  function onCalClick() {
    // Close the popup and open the Cal widget
    onClose();

    // Dispatch a custom event to open the Cal widget
    const event = new CustomEvent("open-cal-widget");
    window.dispatchEvent(event);
  }
}

// Optimize with React.memo to prevent unnecessary re-renders
export default React.memo(SurveyPopup);
